import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import EventForm from './pages/EventForm';
import EventDetail from './pages/EventDetail';
import CheckIn from './pages/CheckIn';
import PublicRegister from './pages/PublicRegister';
import FeedbackForm from './pages/FeedbackForm';
import Reports from './pages/Reports';
import Branding from './pages/Branding';
import Sponsors from './pages/Sponsors';
import Admins from './pages/Admins';
import Analytics from './pages/Analytics';
import AuditPage from './pages/Audit';
import MemberSponsorship from './pages/MemberSponsorship';
import PublicMemberSponsorship from './pages/PublicMemberSponsorship';
import PublicSponsorshipProspectus from './pages/PublicSponsorshipProspectus';
import AnonymousFeedback from './pages/AnonymousFeedback';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {children}
      <ForcePasswordChangeModal />
    </>
  );
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register/:inviteCode" element={<PublicRegister />} />
      <Route path="/feedback/:eventId/:attendeeId" element={<FeedbackForm />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <Events />
        </ProtectedRoute>
      } />
      <Route path="/events/new" element={
        <ProtectedRoute>
          <EventForm />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute>
          <EventDetail />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/edit" element={
        <ProtectedRoute>
          <EventForm />
        </ProtectedRoute>
      } />
      <Route path="/checkin" element={
        <ProtectedRoute>
          <CheckIn />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      } />
      <Route path="/branding" element={
        <ProtectedRoute>
          <Branding />
        </ProtectedRoute>
      } />
      <Route path="/sponsors" element={
        <ProtectedRoute>
          <Sponsors />
        </ProtectedRoute>
      } />
      <Route path="/admins" element={
        <ProtectedRoute>
          <Admins />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/audit" element={
        <ProtectedRoute>
          <AuditPage />
        </ProtectedRoute>
      } />
      <Route path="/member-sponsorship" element={
        <ProtectedRoute>
          <MemberSponsorship />
        </ProtectedRoute>
      } />
      <Route path="/member-sponsorship/submit" element={<PublicMemberSponsorship />} />
      <Route path="/sponsor/:inviteCode" element={<PublicSponsorshipProspectus />} />
      <Route path="/feedback/event/:inviteCode" element={<AnonymousFeedback />} />
      
      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
